import { Link } from "react-router-dom";
import { Clock, Lock } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

/**
 * Top-of-app banner shown when:
 *  - user is on trial (countdown + subtle CTA)
 *  - subscription is expired (red lock + Subscribe CTA)
 * Hidden for active / exempt users.
 */
export default function SubscriptionBanner() {
  const { sub, isTrial, isExpired, daysRemaining } = useSubscription();
  if (!sub) return null;
  if (!isTrial && !isExpired) return null;

  if (isExpired) {
    return (
      <div className="bg-red-600 text-white text-xs px-4 py-2 flex items-center justify-between" data-testid="sub-banner-expired">
        <div className="flex items-center gap-2">
          <Lock className="w-3.5 h-3.5" />
          <span className="font-semibold">Your trial has ended.</span>
          <span className="opacity-90 hidden sm:inline">All edits are locked. Subscribe for ₹100/year to continue.</span>
        </div>
        <Link to="/subscribe" className="bg-white text-red-700 font-semibold px-3 py-1 rounded text-[11px] hover:bg-red-50" data-testid="sub-banner-cta-expired">
          Subscribe Now →
        </Link>
      </div>
    );
  }

  const urgent = daysRemaining != null && daysRemaining <= 2;
  return (
    <div className={`${urgent ? "bg-amber-500 text-amber-950" : "bg-blue-700 text-white"} text-xs px-4 py-2 flex items-center justify-between`} data-testid="sub-banner-trial">
      <div className="flex items-center gap-2">
        <Clock className="w-3.5 h-3.5" />
        <span className="font-semibold">
          {daysRemaining > 0 ? <>Free trial · {daysRemaining} day{daysRemaining === 1 ? "" : "s"} remaining</> : <>Free trial ends today</>}
        </span>
        <span className="opacity-90 hidden sm:inline">— subscribe for ₹100/year to keep your data accessible after the trial.</span>
      </div>
      <Link to="/subscribe" className={`${urgent ? "bg-amber-950 text-amber-100" : "bg-white text-blue-700"} font-semibold px-3 py-1 rounded text-[11px] hover:opacity-90`} data-testid="sub-banner-cta-trial">
        Subscribe →
      </Link>
    </div>
  );
}
